<template>
	<!-- LIST VIEW -->
	<figure v-if="compactMode"
		class="flex gap-3 items-center w-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2 cursor-pointer m-0"
		@click="navigateManage">
		<div class="relative shrink-0 overflow-hidden rounded h-12 w-24 bg-gradient-to-b from-surface-darker to-black">
			<stream-thumb
				v-if="stream"
				class="highlight-thumb w-full h-full"
				:stream="stream"
				:media-pulse="mediaPulse"
				:compact-mode="true"
			/>
			<div
				v-else
				class="w-full h-full flex items-center justify-center text-surface-5">
				<icon-base :width="16" :height="16" :width-view-box="16" :height-view-box="14" class="opacity-50">
					<icon-link />
				</icon-base>
			</div>
		</div>
		<div class="flex flex-col min-w-0 flex-1">
			<div class="flex items-center min-w-0 gap-1">
				<h3
					class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0"
					:title="streamhighlight && streamhighlight.name">
					{{ streamhighlight && streamhighlight.name }}
				</h3>
				<FormButton
					v-if="!showStreamError"
					class="shrink-0"
					isIcon
					type="link"
					size="sm"
					target="_blank"
					:href="getLink()"
					@click.stop>
					<icon-base>
						<icon-link-external />
					</icon-base>
				</FormButton>
			</div>
			<p
				v-if="!showStreamError"
				class="flex items-center gap-1.5 text-tiny text-surface-8 min-w-0 m-0"
				:title="stream && stream.name">
				<icon-base class="shrink-0 text-surface-7" :width="12" :height="12">
					<icon-link />
				</icon-base>
				<span class="truncate">{{ stream && stream.name }}</span>
			</p>
			<p v-else class="flex items-center gap-1.5 text-tiny text-danger min-w-0 m-0">
				<icon-base class="shrink-0" :width="12" :height="12">
					<icon-link />
				</icon-base>
				<span class="truncate">Linked livestream removed</span>
			</p>
		</div>
		<div class="ml-auto flex items-center gap-2 shrink-0" @click.stop>
			<FormButton
				type="danger"
				isIcon
				size="sm"
				aria-label="Delete"
				@click="requestDelete($event)">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
			<FormButton
				v-if="!showStreamError"
				:type="isDisabled ? 'main' : 'secondary'"
				size="sm"
				:is-loading="statusBusy"
				@click.stop.prevent="onToggleClick">
				{{ isDisabled ? 'Enable' : 'Disable' }}
			</FormButton>
		</div>
		<confirm-modal
			:modal-id="'modal-highlight-disable-' + streamhighlight._id"
			title="Disable Live Clipping?"
			message="Recording stops while Live Clipping is disabled. Existing recordings stay available until they expire."
			ok-text="Disable"
			cancel-text="Cancel"
			modal-type="secondary"
			@modal-confirm="setDisabled(true)"
		/>
	</figure>

	<!-- GRID VIEW -->
	<figure v-else
		class="flex flex-col w-full bg-surface-2 h-full relative rounded-xl group shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] transition-[box-shadow,transform] duration-200 ease-out hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 m-0">
		<!-- Preview — same chrome as StreamCardView / PlayoutItemV2 -->
		<div
			class="relative shrink-0 overflow-hidden rounded-t-xl w-full aspect-video bg-gradient-to-b from-surface-darker to-black cursor-pointer"
			@click="navigateManage">
			<stream-thumb
				v-if="stream"
				:stream="stream"
				:media-pulse="mediaPulse"
				:compact-mode="false"
				class="w-full h-full object-cover"
			/>
			<div
				v-else
				class="w-full h-full flex flex-col items-center justify-center gap-2 text-surface-5 px-4 text-center">
				<icon-base :width="28" :height="28" :width-view-box="16" :height-view-box="14" class="opacity-50">
					<icon-link />
				</icon-base>
				<span class="text-sm text-surface-6">Linked livestream removed</span>
			</div>
			<div
				class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
				<span
					class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
					Manage
					<icon-base width="14" height="14" width-view-box="24" height-view-box="24" fill="none">
						<icon-arrow-right />
					</icon-base>
				</span>
			</div>
		</div>

		<!-- Caption: title, then the linked-stream meta sharing a row with the actions -->
		<figcaption
			class="flex flex-col gap-2 !p-4 flex-1 cursor-pointer relative"
			@click="navigateManage">
			<div class="flex items-center min-w-0 gap-1">
				<h3
					class="truncate min-w-0 flex-1 text-lg text-light-1 m-0"
					:title="streamhighlight && streamhighlight.name">
					{{ streamhighlight && streamhighlight.name }}
				</h3>
				<FormButton
					v-if="!showStreamError"
					class="shrink-0"
					isIcon
					type="link"
					size="sm"
					target="_blank"
					:href="getLink()"
					@click.stop>
					<icon-base>
						<icon-link-external />
					</icon-base>
				</FormButton>
			</div>

			<div class="flex gap-2 w-full mt-auto min-w-0" :class="showStreamError ? 'items-start' : 'items-center'">
				<p
					v-if="!showStreamError"
					class="flex items-center gap-1.5 text-sm text-surface-8 min-w-0 m-0 flex-1"
					:title="stream && stream.name">
					<icon-base class="shrink-0 text-surface-7" :width="14" :height="14">
						<icon-link />
					</icon-base>
					<span class="truncate">{{ stream && stream.name }}</span>
				</p>
				<p v-else class="flex items-start gap-1.5 text-sm text-danger min-w-0 m-0 flex-1">
					<icon-base class="shrink-0 mt-0.5" :width="14" :height="14">
						<icon-link />
					</icon-base>
					<span>Linked livestream removed. Delete and create new Live Clipping.</span>
				</p>
				<div class="flex items-center gap-2 shrink-0 ml-auto">
					<FormButton
						type="danger"
						isIcon
						size="sm"
						aria-label="Delete"
						:class="showStreamError ? '' : 'transition-opacity duration-200 lg:opacity-0 group-hover:opacity-100 focus:opacity-100'"
						@click="requestDelete($event)">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
					<FormButton
						v-if="!showStreamError"
						:type="isDisabled ? 'main' : 'secondary'"
						size="sm"
						:is-loading="statusBusy"
						@click.stop.prevent="onToggleClick">
						{{ isDisabled ? 'Enable' : 'Disable' }}
					</FormButton>
				</div>
			</div>
		</figcaption>
		<confirm-modal
			:modal-id="'modal-highlight-disable-' + streamhighlight._id"
			title="Disable Live Clipping?"
			message="Recording stops while Live Clipping is disabled. Existing recordings stay available until they expire."
			ok-text="Disable"
			cancel-text="Cancel"
			modal-type="secondary"
			@modal-confirm="setDisabled(true)"
		/>
	</figure>
</template>

<script>
import StreamThumb from '@/components/ui/StreamThumb.vue'
import StreamStatsService from '@/services/StreamStatsService'
import HighlightService from '@/services/HighlightService'
import FormButton from '@/components/Atoms/FormButton.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { showModal } from '@/Utils/modalBus'
import IconBase from '@/components/icon/IconBase.vue'
import IconLink from '@/components/icon/IconLink.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconLinkExternal from '@/components/icon/IconLinkExternal.vue'
import IconArrowRight from '@/components/icon/IconArrowRight.vue'

export default {
	name: 'HighlightCardView',
	components: {
		FormButton,
		ConfirmModal,
		StreamThumb,
		IconBase,
		IconLink,
		IconBin,
		IconLinkExternal,
		IconArrowRight,
	},
	props: {
		streamhighlight: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id')
			}
		},
		compactMode: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			scopeAlive: true,
			randId: Math.random().toString().slice(2),
			statusProcessing: false,
			streamStatus: false,
			mediaPulse: null,
			mediaPulseTimeoutCtrl: null,
			stream: null,
			streamCountdownSecLeft: null,
			startDateMonth: null,
			startDateDay: null,
			notificationEnabled: null,
			posted: 0,
			showStreamError: false,
			isDisabled: false,
			statusBusy: false,
		}
	},
	computed: {
		userEmail() {
			return this.$store.state.User.email
		},
		streamAlive() {
			return this.streamStatus && this.mediaPulse && this.mediaPulse.alive
		},
		userloaded() {
			return this.$store.state.User.userloaded
		},
		activeTeam() {
			return this.$store.state.User.activatedTeam
		},
		memberPermission() {
			return this.activeTeam && this.$store.state.User.memberPermission
		},
	},
	async mounted() {
		if (this.streamhighlight) {
			this.isDisabled = !!this.streamhighlight.isDisabled
			this.stream = this.streamhighlight?.streamobj[0]
			if (!this.stream) {
				this.showStreamError = true
			} else {
				this.streamStatus = this.stream.enabled
				const gethighlight = await HighlightService.getHighlightbyid(this.streamhighlight._id)
				if (gethighlight.clips.length > 0) {
					const clips = gethighlight.clips.filter((c) => c.status == 'completed')
					this.posted = clips.length
				}
			}
		}
		this.subscribeMediaPulse()
	},
	unmounted() {
		this.scopeAlive = false
		this.unsubscribeMediaPulse()
	},
	methods: {
		subscribeMediaPulse() {
			if (!this.stream?.key) return
			const streamKey = this.stream.key
			const self = this
			;(async function loop() {
				if (!self.scopeAlive) return

				let timeoutInterval = 5000
				let shouldUpdatePulse = self.stream.enabled
				if (shouldUpdatePulse && document.hidden) {
					shouldUpdatePulse = false
					timeoutInterval = 6000
				}

				if (shouldUpdatePulse) {
					if (!document.hasFocus()) {
						timeoutInterval = 14000
					}
				}

				if (shouldUpdatePulse) {
					try {
						const pulseObject = await StreamStatsService.getStreamMediaPulse(streamKey)
						self.onMediaPulse(pulseObject)
					} catch (e) { /* ignore pulse errors */ }
				}

				self.mediaPulseTimeoutCtrl = setTimeout(loop.bind(this), timeoutInterval)
			}())
		},
		onMediaPulse(pulse = {}) {
			this.mediaPulse = pulse
			this.$emit('pulse-update', this.stream._id, pulse)
		},
		unsubscribeMediaPulse() {
			clearTimeout(this.mediaPulseTimeoutCtrl)
		},
		requestDelete(ev) {
			ev.preventDefault()
			ev.stopPropagation()
			this.$emit('delete-highlight', this.streamhighlight)
		},
		onToggleClick() {
			if (this.statusBusy) return
			if (this.isDisabled) {
				this.setDisabled(false)
				return
			}
			showModal('modal-highlight-disable-' + this.streamhighlight._id)
		},
		async setDisabled(disabled) {
			if (this.statusBusy) return
			this.statusBusy = true
			try {
				await HighlightService.checkDisabled(this.streamhighlight._id, disabled)
				this.isDisabled = disabled
				this.streamhighlight.isDisabled = disabled
				this.$notify({
					group: 'success',
					title: disabled ? 'Live Clipping disabled' : 'Live Clipping enabled',
				})
			} catch (err) {
				this.$notify({
					group: 'error',
					title: disabled ? 'Could not disable Live Clipping' : 'Could not enable Live Clipping',
					text: err.message,
				})
			} finally {
				this.statusBusy = false
			}
		},
		getLink() {
			if (this.showStreamError) return
			const resolvedRoute = this.$router.resolve({
				name: 'HighlightSingleManage',
				params: { highlightId: this.streamhighlight._id },
			})
			return resolvedRoute.href
		},
		navigateManage(event) {
			if (this.showStreamError) return
			if (event) {
				const target = event.target
				const interactive = target.closest?.('button, a[href], input, textarea, select, [role="button"]')
				if (interactive) return
			}
			this.$router.push({
				name: 'HighlightSingleManage',
				params: { highlightId: this.streamhighlight._id },
			})
		},
	},
}
</script>

<style scoped>
.highlight-thumb :deep(#thumb-container) {
	aspect-ratio: auto !important;
	height: 100% !important;
}
.highlight-thumb :deep(#thumb-container) video,
.highlight-thumb :deep(#thumb-container) img {
	height: 100% !important;
	width: 100% !important;
	object-fit: cover !important;
}
</style>
